import { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BadgeCheck, Building2, Factory, Globe2, Layers, Scale, Sparkles, ArrowRight,
} from 'lucide-react';
import { useAppSelector } from '../../hooks/redux';
import PageHero from '../../components/PageHero';

const companyIcons = [Building2, Layers, Factory, Globe2, Sparkles, Scale, BadgeCheck, Building2];

export default function CreditRatingPage() {
  const content = useAppSelector((s) => s.geo.content);
  const { ratingPolicy, ratingStandards, ratingScale, companyTypes, countryLabel, hero } = content;
  const location = useLocation();

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (el) {
      window.setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
    }
  }, [location.hash, companyTypes]);

  return (
    <>
      <PageHero
        badge={`${countryLabel} · Credit rating`}
        title={ratingPolicy.title}
        subtitle={ratingPolicy.summary}
      />
      <section style={{ padding: '28px 0 0', background: '#FFFBF8' }}>
        <div className="container-wide">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 16,
              background: 'linear-gradient(135deg, #2D0A1A 0%, #880E4F 55%, #C2185B 100%)',
              borderRadius: 20,
              padding: '22px 26px',
              color: 'white',
            }}
          >
            <div>
              <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.75 }}>
                {hero.comboHighlight}
              </p>
              <p style={{ margin: '6px 0 0', fontSize: 17, fontWeight: 700, lineHeight: 1.45, maxWidth: 640 }}>
                Credit rating is the first step of fundraising on Micus — completing rating unlocks your raise track.
              </p>
            </div>
            <Link
              to="/#start"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 20px',
                borderRadius: 999,
                background: 'white',
                color: '#C2185B',
                fontWeight: 800,
                fontSize: 14,
                textDecoration: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              Start rating & raise <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      <section style={{ padding: '64px 0', background: '#FFFBF8' }}>
        <div className="container-wide">
          <div style={{ marginBottom: 28, maxWidth: 640 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B' }}>
              Rating standards · {countryLabel}
            </p>
            <h2 style={{ margin: '8px 0 0', fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em' }}>
              Standards that match your market
            </h2>
            <p style={{ margin: '8px 0 0', fontSize: 15, color: '#6B3A52', lineHeight: 1.65 }}>
              Micus does not apply a one-size-fits-all scorecard. Your active market under Universal loads the methodology, data rules, and partner packaging for {countryLabel}.
            </p>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
            {ratingStandards.map((std, i) => (
              <motion.div
                key={std.id}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                style={{
                  background: 'white',
                  borderRadius: 18,
                  border: '1px solid #F0C8D8',
                  padding: 24,
                  boxShadow: i === 0 ? '0 12px 40px rgba(194,24,91,0.10)' : undefined,
                  outline: i === 0 ? '2px solid rgba(194,24,91,0.25)' : undefined,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#C2185B' }}>
                    {std.countryFocus}
                  </span>
                  {i === 0 && (
                    <span style={{ fontSize: 10, fontWeight: 800, background: '#FFF0F5', color: '#C2185B', padding: '4px 10px', borderRadius: 999 }}>
                      Primary here
                    </span>
                  )}
                </div>
                <h3 style={{ margin: '0 0 8px', fontSize: 17, fontWeight: 800 }}>{std.name}</h3>
                <p style={{ margin: '0 0 14px', fontSize: 14, color: '#6B3A52', lineHeight: 1.65 }}>{std.summary}</p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {std.highlights.map((h) => (
                    <span key={h} style={{ fontSize: 11.5, fontWeight: 600, color: '#6B3A52', background: '#FFFBF8', border: '1px solid #F0C8D8', borderRadius: 999, padding: '4px 10px' }}>
                      {h}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 0', background: 'white', borderTop: '1px solid #F0C8D8' }}>
        <div className="container-wide">
          <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B' }}>
            Micus scale
          </p>
          <h2 style={{ margin: '8px 0 8px', fontSize: 28, fontWeight: 900 }}>M-AAA to M-D</h2>
          <p style={{ margin: '0 0 24px', fontSize: 15, color: '#6B3A52', maxWidth: 640, lineHeight: 1.65 }}>
            An ordinal opinion of relative credit risk for facilitation. Not a consumer score, and not a published regulated rating unless local authorisation applies.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
            {ratingScale.map((band) => (
              <div key={band.code} style={{ border: '1px solid #F0C8D8', borderRadius: 14, padding: 16, background: '#FFFBF8' }}>
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 16, fontWeight: 900, color: '#C2185B' }}>{band.code}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#9B6E7E' }}>{band.label}</span>
                </div>
                <p style={{ margin: '8px 0 0', fontSize: 13, color: '#4A2535', lineHeight: 1.55 }}>{band.meaning}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="companies" style={{ padding: '64px 0', background: '#FFF0F5', scrollMarginTop: 120 }}>
        <div className="container-wide">
          <p style={{ margin: 0, fontSize: 11, fontWeight: 800, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#C2185B' }}>
            Who we rate
          </p>
          <h2 style={{ margin: '8px 0 8px', fontSize: 28, fontWeight: 900 }}>Company types we understand</h2>
          <p style={{ margin: '0 0 28px', fontSize: 15, color: '#6B3A52', maxWidth: 620, lineHeight: 1.65 }}>
            Different businesses need different credit stories. Micus explains what we emphasise for each type — then maps you to the right partner appetite.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
            {companyTypes.map((ct, i) => {
              const Icon = companyIcons[i % companyIcons.length];
              return (
                <div
                  id={`company-${ct.id}`}
                  key={ct.id}
                  style={{ background: 'white', borderRadius: 18, border: '1px solid #F0C8D8', padding: 24, scrollMarginTop: 130 }}
                >
                  <div style={{ width: 42, height: 42, borderRadius: 12, background: 'linear-gradient(135deg,#C2185B,#E91E63)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                    <Icon size={18} color="#fff" />
                  </div>
                  <h3 style={{ margin: '0 0 4px', fontSize: 17, fontWeight: 800 }}>{ct.title}</h3>
                  <p style={{ margin: '0 0 10px', fontSize: 12, fontWeight: 700, color: '#C2185B' }}>{ct.subtitle}</p>
                  <p style={{ margin: '0 0 14px', fontSize: 14, color: '#6B3A52', lineHeight: 1.65 }}>{ct.description}</p>
                  <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {ct.focus.map((f) => (
                      <li key={f} style={{ fontSize: 11.5, fontWeight: 600, color: '#6B3A52', background: '#FFFBF8', border: '1px solid #F0C8D8', borderRadius: 999, padding: '4px 10px' }}>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 0 72px', background: '#FFFBF8' }}>
        <div className="container-wide" style={{ maxWidth: 860 }}>
          <h2 style={{ fontSize: 24, fontWeight: 900, margin: '0 0 16px' }}>What we check</h2>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {ratingPolicy.points.map((item) => (
              <li key={item} style={{ display: 'flex', gap: 12, fontSize: 15, color: '#4A2535', lineHeight: 1.75, marginBottom: 12, background: 'white', border: '1px solid #F0C8D8', borderRadius: 14, padding: '14px 18px' }}>
                <BadgeCheck size={18} color="#C2185B" style={{ flexShrink: 0, marginTop: 2 }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </>
  );
}
